import { useState, type FormEvent } from "react";
import type { ItemProps } from "@bedrock/domain";

type WebClipProps = Extract<ItemProps, { kind: "web-clip" }>;

export function parseWebClipUrl(value: string): URL {
  const trimmed = value.trim();
  if (!trimmed) throw new Error("Enter a web address.");
  const hasProtocol = /^[a-z][a-z\d+.-]*:/i.test(trimmed);
  if (!hasProtocol && !/^(?:[a-z\d-]+\.)+[a-z\d-]{2,}(?::\d+)?(?:[/?#]|$)/i.test(trimmed)) {
    throw new Error("Enter a valid public web address.");
  }
  const candidate = hasProtocol ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    throw new Error("Enter a valid public web address.");
  }
  if ((url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password) {
    throw new Error("Enter a public HTTP or HTTPS address.");
  }
  return url;
}

export function WebClipForm({ onSubmit, onCancel, initialValue = "" }: { onSubmit: (url: URL) => Promise<void>; onCancel: () => void; initialValue?: string }) {
  const initialProtocol = initialValue.match(/^\s*(https?:\/\/)/i);
  const [entry, setEntry] = useState({
    protocol: initialProtocol?.[1]?.toLowerCase() ?? "https://",
    address: initialProtocol ? initialValue.slice(initialProtocol[0].length) : initialValue
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    let url: URL;
    try {
      const address = entry.address.trim();
      url = parseWebClipUrl(address ? `${entry.protocol}${address}` : "");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Enter a valid public web address.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(url);
    } catch {
      setError("The clip could not be saved. Please try again.");
      setSubmitting(false);
    }
  };
  return <form className={`web-clip-entry ${submitting ? "is-submitting" : ""}`} aria-busy={submitting} noValidate onSubmit={(event) => void submit(event)} onKeyDown={(event) => {
    if (event.key !== "Escape" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  }}>
    <label className="web-clip-entry__label" htmlFor="bedrock-web-clip-url">Web address</label>
    <div className="web-clip-entry__field">
      <span aria-hidden="true">{entry.protocol}</span>
      <input id="bedrock-web-clip-url" autoFocus disabled={submitting} inputMode="url" type="text" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={entry.address} onChange={(event) => {
        const nextAddress = event.target.value;
        const nextProtocol = nextAddress.match(/^(https?:\/\/)/i);
        const nextScheme = nextProtocol?.[1];
        setEntry((current) => nextScheme ? { protocol: nextScheme.toLowerCase(), address: nextAddress.slice(nextScheme.length) } : { ...current, address: nextAddress });
      }} placeholder="website.com" aria-invalid={Boolean(error)} aria-describedby={error ? "bedrock-web-clip-error" : "bedrock-web-clip-instructions"}/>
    </div>
    <span className="web-clip-entry__instructions" id="bedrock-web-clip-instructions">Press Enter to save the clip or Escape to go back.</span>
    {error && <p id="bedrock-web-clip-error" role="alert">{error}</p>}
  </form>;
}

export function CanvasWebClip({ props, onOpen }: { props: WebClipProps; onOpen: () => void }) {
  return <button type="button" className={`item-clip item-clip--${props.status}`} aria-label={`Preview ${props.title || props.siteName || "website"}`} onPointerDown={(event) => event.preventDefault()} onClick={(event) => { event.preventDefault(); if (event.detail === 0) onOpen(); }}>
    {props.previewUrl && <img className="item-clip__preview" src={props.previewUrl} alt="" draggable={false}/>}
    {!props.previewUrl && <div className="item-clip__body"><span>{props.siteName || "WEB CLIP"}</span><h3>{props.title || "Capturing page…"}</h3><p>{props.description}</p><code>{new URL(props.url).hostname}</code></div>}
  </button>;
}
